import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ChatText, FloppyDisk } from "@phosphor-icons/react";

const FIELDS = [
  { key: "booked", label: "Booking confirmation", hint: "Emailed (with your confirmation) when a job is scheduled." },
  { key: "reminder", label: "Day-before reminder", hint: "Emailed automatically the day before a scheduled job." },
  { key: "on_my_way", label: "On my way", hint: "Emailed when a crew taps 'On my way' with an ETA." },
  { key: "completed", label: "Job completed / thank-you", hint: "Emailed (with your confirmation) when a job is marked complete." },
];
const PLACEHOLDERS = ["{first_name}", "{company}", "{service}", "{date}", "{time_clause}", "{eta}", "{portal_url}", "{address}"];

export default function Settings() {
  const [templates, setTemplates] = useState({});
  const [toggles, setToggles] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.notifSettings().then((d) => { setTemplates(d.templates); setToggles(d.toggles); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.saveNotifSettings({ templates, toggles }); toast.success("Settings saved"); }
    catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-slate-400">Loading settings…</div>;

  return (
    <div className="p-4 sm:p-8 max-w-3xl">
      <PageHeader title="Settings" subtitle="Automatic customer email updates"
        actions={<Button onClick={save} disabled={saving} className="bg-[#00B4D8] hover:bg-cyan-600" data-testid="save-settings"><FloppyDisk size={16} className="mr-1.5" />{saving ? "Saving…" : "Save"}</Button>} />

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 text-xs text-slate-500">
        <div className="font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5"><ChatText size={15} weight="duotone" className="text-[#00B4D8]" /> Available placeholders</div>
        <div className="flex flex-wrap gap-1.5">
          {PLACEHOLDERS.map((p) => <code key={p} className="bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{p}</code>)}
        </div>
      </div>

      <div className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`setting-${f.key}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">{f.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{f.hint}</div>
              </div>
              <Switch checked={!!toggles[f.key]} onCheckedChange={(v) => setToggles((t) => ({ ...t, [f.key]: v }))} data-testid={`toggle-${f.key}`} />
            </div>
            <textarea
              value={templates[f.key] || ""} onChange={(e) => setTemplates((t) => ({ ...t, [f.key]: e.target.value }))}
              rows={3} disabled={!toggles[f.key]} data-testid={`template-${f.key}`}
              className="mt-3 w-full text-sm rounded-lg border border-slate-300 p-3 focus:outline-none focus:ring-2 focus:ring-[#00B4D8]/40 resize-none disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
